import { cloneElement, isValidElement, memo } from 'react';
import { Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { UNAUTHORIZED_MODES } from '../../constants/permissionMode';
import { usePermission } from '../../hooks/usePermission';

/**
 * Gates children by permission. Allowed: children. Loading: nothing (no flash). Denied: the
 * `fallback` if given, else by mode: `hide` renders nothing, `lock` renders the children
 * disabled with the no-access explanation.
 *
 * @param {object} props
 * @param {string} props.permission A value of `PERMISSIONS`.
 * @param {'hide'|'lock'} [props.mode] Per-call override of the default mode.
 * @param {import('react').ReactNode} [props.fallback]
 */
const Can = ({ permission, mode: modeOverride, fallback = null, children }) => {
  const { t } = useTranslation('people-access');
  const { allowed, loading, mode } = usePermission(permission, { mode: modeOverride });

  if (loading) return null;
  if (allowed) return children;
  if (fallback) return fallback;
  if (mode !== UNAUTHORIZED_MODES.LOCK) return null;

  const message = t('guard.locked');
  const locked = isValidElement(children) ? (
    cloneElement(children, { disabled: true, 'aria-disabled': true })
  ) : (
    <fieldset disabled className="contents">
      {children}
    </fieldset>
  );

  return (
    <Tooltip content={message}>
      {/* A disabled control swallows mouse events and cannot take focus: the wrapper owns hover/focus. */}
      <span tabIndex={0} className="inline-flex cursor-not-allowed opacity-60">
        <span className="pointer-events-none inline-flex">{locked}</span>
        <span className="sr-only">{message}</span>
      </span>
    </Tooltip>
  );
};

export default memo(Can);
